<template>
  <div class="quote-breakdown">
    <ul v-if="ratebookEnabled" class="quote-breakdown-ratebook">
      <!-- BCH+ keeps the yearly BiK values in the tooltip; the on-screen breakdown (comparison_bch_bik_display) is non-BCH+ only -->
      <li :style="netImpactMode ? 'font-weight:bold;' : null">
        <span class="label">
          Average Monthly BiK
          <VDropdown>
            <span class="tooltip-info">?</span>
            <template #popper>
              <div class="popper-content">
                <h6>Monthly Benefit in Kind (BiK)</h6>
                <ul class="plain striped">
                  <li v-for="bik in deloitteBikAmounts" :key="bik.year">
                    <span>Tax year end - {{ bik.year }}</span>
                    <span>£{{ bik.amount }}</span>
                  </li>
                </ul>
              </div>
            </template>
          </VDropdown>
        </span>
        <span class="value">£{{ deloitteAverageMonthlyBik }}</span>
      </li>
      <!-- bikPlusFixedTradeup mode: SS rows for [TRADE UP] grades only -->
      <template v-if="fixedTradeupMode && isTradeupGrade && tradeupRow">
        <li>
          <span class="label">Gross Salary Sacrifice Amount</span>
          <span class="value">£{{ tradeupRow.grossSalarySacrifice }}.00</span>
        </li>
        <li>
          <ul style="margin:0 0 0 20px;font-style:italic;">
            <li>
              <span class="label">Income Tax Saved on SS</span>
              <span class="value">- £{{ tradeupRow.incomeTaxSaved }}.00</span>
            </li>
            <li>
              <span class="label">National Insurance Saved on SS</span>
              <span class="value">- £{{ tradeupRow.nationalInsuranceSaved }}.00</span>
            </li>
            <li>
              <span class="label">Net Cost of Salary Sacrifice Tradeup</span>
              <span class="value">£{{ tradeupRow.netCost }}.00</span>
            </li>
          </ul>
        </li>
      </template>
      <!-- bikPlusNetImpact mode: SS rows always rendered (£0.00 when not triggered); labels match bikPlusFixedTradeup -->
      <template v-if="netImpactMode">
        <li>
          <span class="label">Gross Salary Sacrifice Amount</span>
          <span class="value">£{{ netImpactBreakdown.salarySacrifice.toFixed(2) }}</span>
        </li>
        <li>
          <ul style="margin:0 0 0 20px;font-style:italic;">
            <li>
              <span class="label">Income Tax Saved on SS</span>
              <span class="value">- £{{ netImpactBreakdown.incomeTaxSaved.toFixed(2) }}</span>
            </li>
            <li>
              <span class="label">National Insurance Saved on SS</span>
              <span class="value">- £{{ netImpactBreakdown.nationalInsuranceSaved.toFixed(2) }}</span>
            </li>
            <li style="font-weight:bold;">
              <span class="label">Net Cost of Salary Sacrifice Tradeup</span>
              <span class="value">£{{ netImpactBreakdown.netImpact.toFixed(2) }}</span>
            </li>
          </ul>
        </li>
        <li v-if="netImpactBreakdown.exceedsMaxTradeup" class="ratebook-remaining over" style="font-weight:bold;padding:6px 10px;background:#c0392b;color:#fff;border-radius:3px;">
          <span class="label"></span>
          <span class="value">
            You have exceeded your maximum trade up of £{{ netImpactBreakdown.maxTradeup.toFixed(2) }}
          </span>
        </li>
      </template>
      <!-- Default mode: Trade Up/Down and One-Off Contribution rows -->
      <li v-if="!fixedTradeupMode && !netImpactMode && RatebookHelper.tradeUpDownRawValue(ratebook)">
        <span class="label">
          Trade
          <template v-if="RatebookHelper.tradeUpDownRawValue(ratebook) < 0">Down</template>
          <template v-else>Up</template>
        </span>
        <span class="value">£{{RatebookHelper.tradeUpDown(ratebook, term)}}</span>
      </li>
      <li v-if="!fixedTradeupMode && !netImpactMode && RatebookHelper.oneOffContribution(ratebook, term)" :class="{'has-remaining':currentProduct.your_shortlist_display_remaining_grade_level_budget && oocRemaining}">
        <span class="label">One-Off Contribution</span>
        <span class="value">£{{ RatebookHelper.oneOffContribution(ratebook, term).toFixed(2) }}</span>
      </li>
      <li v-if="!fixedTradeupMode && !netImpactMode && currentProduct.your_shortlist_display_remaining_grade_level_budget && oocRemaining !== null" class="ratebook-remaining" :class="{'over': oocRemaining < 0}" style="margin-top:10px;">
        <span class="label"></span>
        <span class="value">
          Remaining One-off Contribution Budget: £{{oocRemaining.toFixed(2)}}
        </span>
      </li>
      <!-- Remaining WLC for fixedTradeupMode -->
      <remaining-wlc-row
        v-if="fixedTradeupMode && wlcRemaining !== null"
        :remaining="wlcRemaining"
        :tooltip="currentProduct?.tooltip_rwlc?.trim() || null"
      />
      <!-- Remaining WLC for bikPlusNetImpact mode -->
      <remaining-wlc-row
        v-if="netImpactMode"
        :remaining="netImpactBreakdown.wlcRemaining"
        :tooltip="currentProduct?.tooltip_rwlc?.trim() || null"
      />
      <li v-if="RatebookHelper.netCost(ratebook, term, ratebookNetCostCalcMethod, ratebookContext)" class="ratebook-netcost">
        <span class="label">
          Estimated NET Monthly Cost
          <VDropdown>
            <span class="tooltip-info white">?</span>
            <template #popper>
              <div class="popper-content">
                <p>This cost is the average monthly BIK, plus the cost of any <br>
                  one-off contribution spread over the contract duration. As per <br>
                  the policy, the one-off contribution will need to be paid in full <br>
                  before the vehicle is delivered.</p>
              </div>
            </template>
          </VDropdown>
        </span>
        <span class="value">£{{ RatebookHelper.netCost(ratebook, term, ratebookNetCostCalcMethod, ratebookContext) }}</span>
      </li>
      <!--Use jaama rental price as per Chris Paddick 12/6/25-->
      <li v-if="!fixedTradeupMode && !netImpactMode && currentProduct.your_shortlist_display_vehicle_rental_cost" class="ratebook-othercost">
        <span class="label">Vehicle Rental</span>
        <span class="value">£{{ activeQuote.details.pricing.totalExVAT }}<!--{{ RatebookHelper.vehicleRental(ratebook, term) }}--></span>
      </li>
      <li v-if="!fixedTradeupMode && !netImpactMode && currentProduct.your_shortlist_display_estimated_whole_life_cost && RatebookHelper.wholeLifeCost(ratebook, term)" class="ratebook-othercost" :class="{'has-remaining':currentProduct.your_shortlist_display_remaining_grade_level_budget && wlcRemaining}">
        <span class="label">
          Whole Life Cost
        </span>
        <span class="value">£{{ RatebookHelper.wholeLifeCost(ratebook, term) }}</span>
      </li>
      <!-- Remaining WLC for default mode -->
      <remaining-wlc-row
        v-if="!fixedTradeupMode && !netImpactMode && currentProduct.your_shortlist_display_remaining_grade_level_budget && wlcRemaining !== null"
        :remaining="wlcRemaining"
        :tooltip="currentProduct?.tooltip_rwlc?.trim() || null"
      />
    </ul>
    <ul v-else class="quote-breakdown-list-bch quote-breakdown-list">
      <template v-if="showDeloitteBik">
        <li>
          <span class="cost">
            <span class="label">Average Monthly BiK</span>
            <span class="value">£{{ deloitteAverageMonthlyBik }}</span>
          </span>
        </li>
        <li v-if="deloitteBikAmounts.length">
          <ul class="bik-breakdown">
            <li v-for="bik in deloitteBikAmounts" :key="bik.year">
              <span class="cost">
                <span class="label">Tax year end - {{ bik.year }}</span>
                <span class="value">£{{ bik.amount }}</span>
              </span>
            </li>
          </ul>
        </li>
      </template>
      <li v-else>
        <span class="cost">
          <span class="label">Monthly BiK</span>
          <span class="value">£{{ monthlyBik }}</span>
        </span>
      </li>
      <li v-if="costConfiguration.showContribution === 'monthly'">
        <span class="cost">
          <span class="label">Monthly Contribution</span>
          <span class="value">//TODO</span>
        </span>
      </li>
      <li v-if="costConfiguration.showRental === 'totaleffective'">
        <span class="cost">
          <span class="label">Vehicle Rental</span>
          <span class="value">£{{ activeQuote.details.lifeCosts.totalEffectiveRental }}</span>
        </span>
      </li>
      <li v-if="costConfiguration.showRental === 'rentalexvat'">
        <span class="cost">
          <span class="label">Vehicle Rental</span>
          <span class="value">£{{ activeQuote.details.pricing.totalExVAT }}</span>
        </span>
      </li>
      <li v-if="costConfiguration.showContribution === 'oneoff'">
        <span class="cost">
          <span class="label">One Off Contribution</span>
          <span class="value">//TODO</span>
        </span>
      </li>
      <li class="comparison-divider"></li>
      <li>
        <span class="cost total">
          <span class="label">Total Monthly Cost</span>
          <span class="value">£{{ monthlyCost }}</span>
        </span>
      </li>
      <li class="comparison-divider"></li>
    </ul>

    <template v-if="ratebookDebug">
      <h5>Ratebook Data:</h5>
      <ul class="ratebook">
        <li v-for="(item, index) in ratebook" :key="index" style="padding:5px;border-bottom:1px solid #333;">
          <template v-if="index !== 'requestJSON'">
            <span>{{ index }}: </span>
            <ul v-if="typeof item === 'object' && item !== null">
              <li v-for="(subItem, subIndex) in item" :key="subIndex">
                <span>{{ subIndex }}: </span>
                <span><b>{{ subItem === null ? 'NULL' : subItem }}</b></span>
              </li>
            </ul>
            <span v-else><b>{{ item === null ? 'NULL' : item }}</b></span>
          </template>
        </li>
      </ul>
    </template>
  </div>

</template>

<script type="text/javascript">

import LoadingIcon from "@/components/loading-icon";
import RemainingWlcRow from "@/components/car-quote/bch/remaining-wlc-row";
import VehicleCosts from '@/helpers/vehicle-costs';
import {useRoute} from "vue-router";
import RatebookHelper from "@/helpers/ratebook";
import BikPlusFixedTradeup from "@/helpers/net-cost/bik-plus-fixed-tradeup";
import BikPlusNetImpact from "@/helpers/net-cost/bik-plus-net-impact";

export default {
  name: 'bch-quote-breakdown',
  components: {LoadingIcon, RemainingWlcRow},
  props: [
    'quote',
    'derivative',
    'workingQuote',
    'quoteCosts',
    'driverProfile',
    'type',
    'showFuel',
    'showTraditionalCost',
    'orderSummaryView',
    'averagePencePerMile',
    'totalEstimatedElectricCostPerMonth',
    'adminPortalData',
  ],
  data() {
    return {
      ratebookDebug: false,
    }
  },
  setup() {
    const route = useRoute()
    return {route}
  },

  mounted() {
  },

  created() {
    this.getVehicleBikByRate = VehicleCosts.getVehicleBikByRate;
  },
  computed: {
    RatebookHelper() {
      return RatebookHelper
    },
    // A basic, non-definitive way of checking if a user has a ratebook for this vehicle, and thus should see the ratebook values
    ratebook() {
      return this.quote.taxCalculations;
    },
    ratebookEnabled() {
      return this.RatebookHelper.isRatebookEnabledGrade(this.quote.details.driverGradeName);
    },
    driverGrade() {
      return this.quote.driverGrade;
    },
    term() {
      return this.quote.definition.plan.term;
    },
    activeQuote() {
      return this.workingQuote ? this.workingQuote : this.quote;
    },
    monthlyBik() {
      return Number(this.getVehicleBikByRate(this.activeQuote.details, this.ratePercent)).toFixed(2);
    },
    showDeloitteAvgBikBreakdown() {
      return this.RatebookHelper.isDeloitteAvgBreakdownBikDisplay(this.currentProduct);
    },
    deloitteAverageMonthlyBik() {
      return this.RatebookHelper.averageMonthlyBik(this.ratebook, this.term);
    },
    deloitteBikAmounts() {
      return this.RatebookHelper.bikAmounts(this.ratebook);
    },
    /** True when the Deloitte block can actually render (mode + figure present); otherwise the existing Monthly BiK row shows. */
    showDeloitteBik() {
      return this.showDeloitteAvgBikBreakdown && this.deloitteAverageMonthlyBik !== null;
    },
    /** BiK figure feeding Total Monthly Cost - always the figure that is displayed. */
    bikForTotal() {
      return this.showDeloitteBik ? parseFloat(this.deloitteAverageMonthlyBik) : parseFloat(this.monthlyBik);
    },
    costConfiguration() {
      return {
        showContribution: this.productData.display_settings_show_monthly_contribution ? this.productData.display_settings_monthly_contribution_type : false,
        showRental: this.productData.display_settings_show_vehicle_rental ? this.productData.display_settings_vehicle_rental_type : false,
      }
    },
    productData() {
      return this.adminPortalData.content.products['bch'][0];
    },
    monthlyCost() { //TODO
      let cost = this.bikForTotal;
      if (this.costConfiguration.showContribution === 'monthly') {
        cost += 0; //TODO
      }
      return cost.toFixed(2);
    },
    currentProduct() {
      return this.adminPortalData.content.products['bch'][0];
    },
    showBchWlc() {
      return RatebookHelper.showWholeLifeCosts(this.currentProduct);
    },
    showBchVehicleRental() {
      return RatebookHelper.showVehicleRental(this.currentProduct);
    },
    oocRemaining() {
      return this.RatebookHelper.oneOffContributionRemaining(this.ratebook, this.driverGrade?.deloitteFilters?.employeeCostCapitalContributionTo, this.term);
    },
    wlcRemaining() {
      return this.RatebookHelper.wholeLifeCostRemaining(this.ratebook, this.driverGrade?.deloitteFilters?.companyCostTotalTo, this.term);
    },
    ratebookNetCostCalcMethod() {
      return this.currentProduct?.calcs_net_cost_display || null;
    },
    fixedTradeupMode() {
      return BikPlusFixedTradeup.isEnabled(this.currentProduct);
    },
    isTradeupGrade() {
      return BikPlusFixedTradeup.isTradeupGrade(this.quote.details.driverGradeName);
    },
    ratePercent() {
      return this.driverProfile?.incomeTaxBand?.ratePercent;
    },
    ratebookContext() {
      return {
        ratePercent: this.ratePercent,
        driverGradeName: this.quote.details.driverGradeName,
        driverGrade: this.driverGrade,
      };
    },
    tradeupRow() {
      return BikPlusFixedTradeup.tradeupRow(this.ratePercent);
    },
    netImpactMode() {
      return BikPlusNetImpact.isEnabled(this.currentProduct);
    },
    netImpactBreakdown() {
      return BikPlusNetImpact.breakdown(this.ratebook, this.term, this.ratePercent, this.driverGrade);
    },
  },
  methods: {
    getMonthlyCost(value) {
      return (Number(value) / this.term);
    },

    getInCurrency(value, currency = "£") {
      return currency + Number(value).toFixed(2);
    }

  }
}
</script>

<style scoped lang="scss">
ul.quote-breakdown-list-bch {
  float: none;
  width: 100%;

  li {
    width: 100%;
    display: block;
    margin-bottom: 6px;

    &:nth-child(even) {
      padding-left: 0;
    }

    &:nth-child(odd) {
      padding-right: 0;
    }

    &.comparison-divider {
      border-top: 1px solid #C8D9DE;
      border-bottom: 1px solid #C8D9DE;
      margin-top: 20px !important;
      margin-bottom: 20px !important;
    }
  }

  span.cost {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;

    span {
      &.label {
        font-size: 14px;
      }

      &.value {
        font-size: 18px !important;
      }
    }

    &.total {
      span.label {
        font-weight: bold;
        font-size: 16px;
      }

      span.value {
        font-weight: bold;
      }
    }
  }

  ul.bik-breakdown {
    // Indent with padding, not margin, so the nested list stays inside its container and the values
    // line up with the Average Monthly BiK value above (margin overflowed on the narrower order sidebar)
    margin: 0;
    padding-left: 20px;
    width: 100%;
    box-sizing: border-box;
    font-style: italic;

    // Only slightly smaller than the Average Monthly BiK row, matching the indented sub-rows on the comparison breakdown
    li span.cost {
      span.label {
        font-size: 13px;
      }

      span.value {
        font-size: 16px !important;
      }
    }
  }
}
</style>
